(() => {
  "use strict";

  if (window.CurlCartDrawer?.mount) {
    window.CurlCartDrawer.mount();
    return;
  }

  const rootSelector = "[data-curl-cart-drawer]";
  const drawerSelector = ".cart-drawer";
  const overlaySelector = ".cart-overlay";
  const openSelector = "[data-curl-cart-open], .js-drawer-open-button-right[aria-controls='CartDrawer'], .header__icon--cart, button[aria-label^='Open bag with']";
  const productFormSelector = ".pdp-product-form[data-type='add-to-cart-form']";
  const productButtonSelector = "button.pdp-primary[name='add']";
  const cartMutationPattern = /(^|\/)cart\/(add|change|update|clear)(?:\.js)?\/?$/;
  const nativeFetch = window.fetch.bind(window);
  const nativeXhrOpen = XMLHttpRequest.prototype.open;
  const nativeXhrSend = XMLHttpRequest.prototype.send;
  let previousFocus = null;
  let refreshTimer = null;
  let refreshRunning = false;
  let refreshAgain = false;
  let openAfterRefresh = false;
  const productButtonTimers = new WeakMap();

  const getRoot = () => document.querySelector(rootSelector);
  const getDrawer = () => getRoot()?.querySelector(drawerSelector);
  const getOverlay = () => getRoot()?.querySelector(overlaySelector);

  const setProductFormLoading = (form, loading) => {
    const button = form?.querySelector(productButtonSelector);
    if (!button) return;

    const previousTimer = productButtonTimers.get(button);
    if (previousTimer) window.clearTimeout(previousTimer);

    if (loading) {
      button.disabled = true;
      button.classList.add("is-loading");
      button.setAttribute("aria-busy", "true");
      form.setAttribute("aria-busy", "true");
      const timer = window.setTimeout(() => setProductFormLoading(form, false), 20000);
      productButtonTimers.set(button, timer);
      return;
    }

    productButtonTimers.delete(button);
    button.classList.remove("is-loading");
    button.removeAttribute("aria-busy");
    form.removeAttribute("aria-busy");

    const select = form.querySelector("select[name='id']");
    const selectedOption = select?.options[select.selectedIndex];
    button.disabled = selectedOption ? selectedOption.dataset.available !== "true" : false;
  };

  const releaseProductButtons = () => {
    document.querySelectorAll(productFormSelector).forEach((form) => setProductFormLoading(form, false));
  };

  const dispatchCartError = (error) => {
    releaseProductButtons();
    document.dispatchEvent(new CustomEvent("curl:cart-drawer:error", { detail: { error } }));
  };

  const getCartAction = (value, method = "GET") => {
    if (!value || String(method).toUpperCase() === "GET") return "";

    try {
      const url = new URL(typeof value === "string" ? value : value.url, window.location.origin);
      const match = url.pathname.match(cartMutationPattern);
      return match ? match[2] : "";
    } catch (error) {
      return "";
    }
  };

  const setExpanded = (expanded) => {
    document.querySelectorAll(openSelector).forEach((trigger) => {
      trigger.setAttribute("aria-expanded", String(expanded));
    });
  };

  const syncCartCount = () => {
    const root = getRoot();
    if (!root) return;

    const count = Number(root.dataset.cartItemCount || 0);

    document.querySelectorAll(".cart-count, [data-cart-count]").forEach((element) => {
      element.textContent = count > 99 ? "99+" : String(count);
    });

    document.querySelectorAll(".cart-link__bubble").forEach((element) => {
      element.classList.toggle("cart-link__bubble--visible", count > 0);
    });

    document.querySelectorAll("button[aria-label^='Open bag with']").forEach((button) => {
      const label = button.getAttribute("aria-label") || "";
      button.setAttribute("aria-label", label.replace(/\d+/, String(count)));
      button.textContent = button.textContent.replace(/(BAG\s*·\s*)\d+/i, `$1${count}`);
    });
  };

  const open = (focusClose = true) => {
    const drawer = getDrawer();
    const overlay = getOverlay();
    if (!drawer || !overlay) return;

    if (!drawer.classList.contains("is-open")) {
      previousFocus = document.activeElement;
    }

    drawer.classList.add("is-open");
    overlay.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    overlay.setAttribute("aria-hidden", "false");
    document.body.classList.add("curl-cart-drawer-open");
    setExpanded(true);

    if (focusClose) {
      drawer.querySelector(".cart-close")?.focus({ preventScroll: true });
    }
  };

  const close = () => {
    const drawer = getDrawer();
    const overlay = getOverlay();
    if (!drawer || !overlay) return;

    const themeDrawer = window.timber?.RightDrawer;
    if (themeDrawer?.drawerIsOpen && typeof themeDrawer.close === "function") {
      themeDrawer.close();
    }

    drawer.classList.remove("is-open", "js-drawer-open");
    overlay.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    overlay.setAttribute("aria-hidden", "true");
    document.body.classList.remove("curl-cart-drawer-open", "js-drawer-open", "js-drawer-open-right");
    setExpanded(false);

    if (previousFocus instanceof HTMLElement && document.contains(previousFocus)) {
      previousFocus.focus({ preventScroll: true });
    }
  };

  const sectionUrl = (sectionId) => {
    const rootUrl = window.Shopify?.routes?.root || "/";
    const url = new URL(rootUrl, window.location.origin);
    url.searchParams.set("section_id", sectionId);
    url.searchParams.set("_", Date.now().toString());
    return url.toString();
  };

  const refresh = async (options = {}) => {
    openAfterRefresh = openAfterRefresh || Boolean(options.open);

    if (refreshRunning) {
      refreshAgain = true;
      return;
    }

    const currentRoot = getRoot();
    const sectionId = currentRoot?.dataset.sectionId;
    if (!currentRoot || !sectionId) {
      releaseProductButtons();
      return;
    }

    const wasOpen = Boolean(getDrawer()?.classList.contains("is-open"));
    const shouldOpen = openAfterRefresh || wasOpen;
    const shouldFocus = openAfterRefresh && !wasOpen;
    openAfterRefresh = false;
    refreshRunning = true;
    currentRoot.setAttribute("aria-busy", "true");

    try {
      const response = await nativeFetch(sectionUrl(sectionId), {
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" },
        cache: "no-store"
      });

      if (!response.ok) throw new Error(`Cart drawer request failed with ${response.status}`);

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, "text/html");
      const nextRoot = documentFragment.querySelector(rootSelector);
      if (!nextRoot) throw new Error("Cart drawer section was not returned");

      currentRoot.replaceWith(nextRoot);
      syncCartCount();

      if (shouldOpen) open(shouldFocus);

      document.dispatchEvent(new CustomEvent("curl:cart-drawer:updated", {
        detail: {
          itemCount: Number(nextRoot.dataset.cartItemCount || 0),
          open: shouldOpen
        }
      }));
      releaseProductButtons();
    } catch (error) {
      currentRoot.removeAttribute("aria-busy");
      dispatchCartError(error);
    } finally {
      refreshRunning = false;

      if (refreshAgain) {
        refreshAgain = false;
        refresh({ open: openAfterRefresh });
      }
    }
  };

  const queueRefresh = (options = {}) => {
    openAfterRefresh = openAfterRefresh || Boolean(options.open);
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => refresh({ open: openAfterRefresh }), 80);
  };

  const changeLine = async (key, quantity) => {
    const drawer = getDrawer();
    drawer?.setAttribute("aria-busy", "true");

    try {
      const response = await nativeFetch(`${window.Shopify?.routes?.root || "/"}cart/change.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) throw new Error(`Cart update failed with ${response.status}`);
      await refresh({ open: true });
    } catch (error) {
      drawer?.removeAttribute("aria-busy");
      dispatchCartError(error);
    }
  };

  const addUpsell = async (button) => {
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    button.disabled = true;
    getDrawer()?.setAttribute("aria-busy", "true");

    try {
      const response = await nativeFetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });

      if (!response.ok) throw new Error(`Upsell add failed with ${response.status}`);
      await refresh({ open: true });
    } catch (error) {
      button.disabled = false;
      getDrawer()?.removeAttribute("aria-busy");
      dispatchCartError(error);
    }
  };

  const handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const closeTrigger = target.closest("[data-curl-cart-close]");
    if (closeTrigger) {
      event.preventDefault();
      event.stopImmediatePropagation();
      close();
      return;
    }

    const openTrigger = target.closest(openSelector);
    if (openTrigger) {
      event.preventDefault();
      event.stopImmediatePropagation();
      open();
      queueRefresh({ open: true });
      return;
    }

    const quantityButton = target.closest("[data-curl-cart-quantity]");
    if (quantityButton) {
      event.preventDefault();
      const key = quantityButton.dataset.cartKey;
      const quantity = Math.max(0, Number(quantityButton.dataset.quantity || 0));
      if (key) changeLine(key, quantity);
      return;
    }

    const removeButton = target.closest("[data-curl-cart-remove]");
    if (removeButton) {
      event.preventDefault();
      const key = removeButton.dataset.cartKey;
      if (key) changeLine(key, 0);
      return;
    }

    const upsellButton = target.closest("[data-curl-upsell-add]");
    if (upsellButton) {
      event.preventDefault();
      addUpsell(upsellButton);
    }
  };

  const handleKeydown = (event) => {
    const drawer = getDrawer();
    if (!drawer?.classList.contains("is-open")) return;

    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== "Tab") return;

    const focusable = [...drawer.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])")]
      .filter((element) => !element.hasAttribute("hidden"));
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleProductSubmit = (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form?.matches(productFormSelector)) return;

    const button = event.submitter?.matches?.(productButtonSelector)
      ? event.submitter
      : form.querySelector(productButtonSelector);
    if (!button || button.disabled) return;

    queueMicrotask(() => setProductFormLoading(form, true));
  };

  const patchNetwork = () => {
    window.fetch = async function(input, init = {}) {
      const requestInit = init || {};
      const method = requestInit.method || (typeof input === "object" && input ? input.method : "GET");
      const action = getCartAction(input, method);

      let response;
      try {
        response = await nativeFetch(input, init);
      } catch (error) {
        if (action) dispatchCartError(error);
        throw error;
      }

      if (action && response.ok) {
        queueRefresh({ open: action === "add" });
      } else if (action) {
        dispatchCartError(new Error(`Cart request failed with ${response.status}`));
      }

      return response;
    };

    XMLHttpRequest.prototype.open = function(method, url) {
      this.curlCartAction = getCartAction(url, method);
      return nativeXhrOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function() {
      if (this.curlCartAction) {
        this.addEventListener("loadend", () => {
          if (this.status >= 200 && this.status < 400) {
            queueRefresh({ open: this.curlCartAction === "add" });
          } else {
            dispatchCartError(new Error(`Cart request failed with ${this.status}`));
          }
        }, { once: true });
      }

      return nativeXhrSend.apply(this, arguments);
    };
  };

  const mount = () => {
    syncCartCount();
    releaseProductButtons();
  };

  document.addEventListener("click", handleClick, true);
  document.addEventListener("submit", handleProductSubmit, true);
  document.addEventListener("keydown", handleKeydown);
  document.addEventListener("cart:updated", () => queueRefresh());
  document.addEventListener("cart:change", () => queueRefresh());
  document.addEventListener("product:added", () => queueRefresh({ open: true }));
  document.addEventListener("shopify:section:load", mount);
  window.addEventListener("pageshow", mount);
  patchNetwork();
  mount();

  window.CurlCartDrawer = { open, close, refresh, mount };
})();
